import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import ApiService from '../ApiService'
import Snackbar from '@material-ui/core/Snackbar'
import removeClass from '../util/RemoveCSS'
import { clinicianIdFromRefreshAuth } from '../util/authUtil'
import MuiAlert from '@material-ui/lab/Alert'
import { parseAxiosError } from '../util/parserHelper'
import Alert from 'react-bootstrap/Alert'

//@ Modules
import ProfileDrawer from './ClinicianProfile/Drawer/ProfileDrawer'
import EditProfile from './ClinicianProfile/Profile/EditProfile'
import ConfirmationModal from './ClinicianProfile/Profile/ConfirmationModal'
import EditLicense from './ClinicianProfile/Licence/EditLicense'
import EditCalendar from './ClinicianProfile/Calendar/EditCalendar'
import ClinicianTemplates from './ClinicianProfile/ClinicianTemplates/ClinicianTemplates'

export const licenceEventProps = {
    errType: 'error',
    infoType: 'info',
    event_clinicianProfileUpdated: 'clinician_profile_updated',
    event_licenseEditWip: 'license_edit_wip',
    event_licenseEditOrAddDiscard: 'license_edit_or_add_discard',
}

export const profileRedirectReason = {
    noLicense: {
        slug: 'noLicense',
        showNum: 3,
        message: 'Please enter your license info before accessing portal',
        bannerVariant: 'danger',
        bannerHeading: 'Access Restricted',
    },
    expiredOrInvalidLicense: {
        slug: 'expiredOrInvalidLicense',
        showNum: 3,
        message: 'Please update license(s) that are invalid or expired',
        bannerVariant: 'danger',
        bannerHeading: 'Access Restricted',
    },
    invalidOrNoCalendar: {
        slug: 'invalidOrNoCalendar',
        showNum: 4,
        message: 'Please add/update calendar before accessing portal',
        bannerVariant: 'warning',
        bannerHeading: 'Action Required',
    },
}

const Profile = ({ isGoLicenseView, isGoCalendarView }) => {
    const location = useLocation()
    const query = new URLSearchParams(location.search);

    //$ Page States
    const [clinicianProfile, setClinicianProfile] = useState([])
    const [isEditLicenceWip, setIsEditLicenceWip] = useState(false)
    const [isAddLicenceWip, setIsAddLicenceWip] = useState(false)
    const [isPersonInfoWip, setIsPersonInfoWip] = useState(false)
    const [licenses, setLicenses] = useState([])
    const [calendarLink, setCalendarLink] = useState('')
    const [profileDetails, setProfileDetails] = useState(null)
    const [show, setShow] = useState(2)
    const [editLicense, setEditLicense] = useState(false)
    const [loading, setLoading] = useState(true)
    const [validated, setValidated] = useState(false)
    const [clinicianId, setClinicianId] = useState(null);

    //$ Modal and Snacks Data
    const [profileModal, setProfileModal] = useState(false)
    const [snackOpen, setSnackOpen] = useState(false)
    const [snackText, setSnackText] = useState('')
    const [snackSeverity, setSnackSeverity] = useState('')

    const [bannerProps, setBannerProps] = useState({})
    const [isShowBanner, setIsShowBanner] = useState(false)

    const handleSnackOpen = (text, severity) => {
        setSnackSeverity(severity)
        setSnackText(text)
        setSnackOpen(true)
    }

    const handleClose = () => {
        setProfileModal(false)
    }

    const handleSnackClose = () => {
        setSnackOpen(false)
    }

    const bannerConditionalRender = () => {
        if (loading || !isShowBanner || Object.entries(bannerProps).length === 0) return null
        return (
            <Alert key={bannerProps.variant} variant={bannerProps.variant}>
                {bannerProps.header && <Alert.Heading>{bannerProps.header}</Alert.Heading>}
                <p>{bannerProps.message}</p>
            </Alert>
        )
    }

    const getNavAndBannerProps = () => {
        if (isGoLicenseView === true) return { showNum: 3, message: null, isRedirect: false }
        if (isGoCalendarView === true) return { showNum: 4, message: null, isRedirect: false }

        const reasonSlug = query.get('reason')
        const matchReason = Object.entries(profileRedirectReason).find(([key, val]) => val.slug === reasonSlug)
        if (!matchReason) return null
        const [, { slug, showNum, message, bannerVariant, bannerHeading }] = matchReason
        return { showNum, message: message, isRedirect: true, bannerVariant, bannerHeading }
    }

    //& PART 1 : Fetch Clinician Profile from ID

    useEffect(() => {
        clinicianIdFromRefreshAuth()
            .then(id => setClinicianId(id));
    }, []);

    useEffect(() => {
        if(!clinicianId) return;
        ;(async () => {
            setLoading(true);
            const res = await ApiService.getClinicianProfile(clinicianId)
                .then(res => setClinicianProfile(res?.data))
                .catch(err => handleSnackOpen(parseAxiosError(err)?.message, 'error'))
                .finally(() => setLoading(false))
        })().then(r => r)

        const navBannerProps = getNavAndBannerProps()
        if (navBannerProps && navBannerProps?.showNum) setShow(navBannerProps?.showNum)
        if (navBannerProps && navBannerProps.isRedirect === true) {
            setIsShowBanner(true)
            const variant = navBannerProps?.bannerVariant ?? 'warning'
            const message = navBannerProps?.message ?? 'No message, report to tech'
            const header = navBannerProps?.bannerHeading ?? 'unknown, report to tech'
            setBannerProps({ variant, message, header })
        }
        //if(showNumMsg && showNumMsg?.message) handleSnackOpen(showNumMsg?.message, 'info');
    }, [clinicianId])

    useEffect(() => {
        setLicenses(clinicianProfile?.licences ?? [])
    }, [clinicianProfile])

    //$ Basic Validate Function
    const basicValidation = e => {
        try {
            e.preventDefault()
            const formData = new FormData(e.target),
                formDataObj = Object.fromEntries(formData.entries())
            setProfileDetails({
                id: clinicianId,
                ...formDataObj,
                hoobies: '',
                emergencyContactName: '',
                emergencyContactPhone: '',
            })
            setProfileModal(true)
        } catch (error) {
            handleSnackOpen(error?.message, 'error')
        }
    }

    //$ Function to Update Profile
    const updateClinicianProfile = async e => {
        if (!profileDetails) {
            handleSnackOpen('No changes to update', 'error')
            return
        }
        setLoading(true)
        const res = await ApiService.updateClinicianProfile(profileDetails)
            .then(res => {
                setClinicianProfile(res?.data)
                handleSnackOpen('Profile has been updated', 'success')
                setIsPersonInfoWip(false)
                setProfileDetails(null)
                setProfileModal(false)
            })
            .catch(err => {
                handleSnackOpen(`Profile update failed. ${parseAxiosError(err)}`, 'error')
            })
            .finally(() => setLoading(false))
    }

    //` Clear Form
    const handleClear = e => {
        document.querySelectorAll('input').forEach(input => (input.value = ''))
        setIsPersonInfoWip(false)
    }

    removeClass() //` Didnt Removed this dont know purpose of this

    //$ Style Overrides for this page
    useEffect(() => {
        document.body.style.background = 'rgba(253, 250, 241, 1)'
        return () => {
            document.body.style.background = '#FDFAF1'
        }
    }, [])

    //$ Add Calendar Link
    const updateClinicianCalendar = async () => {
        setLoading(true)
        await ApiService.updateClinicianCalendar({ calendarLink: calendarLink })
            .then(res => {
                setClinicianProfile(res?.data)
                handleSnackOpen('Calendar Link has been updated', 'success')
            })
            .catch(err => handleSnackOpen(`Calendar Link Update Failed. ${parseAxiosError(err)?.message}`, 'error'))
            .finally(() => setLoading(false))
    }

    const licenseEventListener = event => {
        if (!event) return

        if (!event?.name && (event?.type === licenceEventProps.errType || event?.type === licenceEventProps.infoType)) {
            handleSnackOpen(event?.message ?? 'no message/reason', event?.type)
            return
        }

        if (event?.name === licenceEventProps.event_clinicianProfileUpdated) {
            setClinicianProfile(event?.profile)
            setIsEditLicenceWip(false)
            setIsAddLicenceWip(false)
            handleSnackOpen(event.message, event.type)
            return
        }

        if (event?.name === licenceEventProps.event_licenseEditWip) {
            setIsEditLicenceWip(true)
            return
        }

        if (event?.name === licenceEventProps.event_licenseEditOrAddDiscard) {
            if (event?.license?.isNewAdd === true) {
                setLicenses(licenses.filter(li => li.uniqueId !== event?.license?.uniqueId))
            }
            setIsEditLicenceWip(false)
            setIsAddLicenceWip(false)
            handleSnackOpen(event.message, event.type)
            return
        }
    }

    //& Main View
    return (
        <Row className="mt-4">
            {/* //& Loading View */}
            {loading && (
                <Row style={styles.container}>
                    <Spinner animation="border" role="status">
                        <span className="sr-only">Loading...</span>
                    </Spinner>
                </Row>
            )}

            {/* //& Main Container */}
            {/* //$ Profile Drawer */}
            <Col style={styles.drawerContainer}>
                <ProfileDrawer
                    clinicianProfile={clinicianProfile}
                    show={show}
                    setShow={setShow}
                    editLicense={editLicense}
                    isAddLicenceWip={isAddLicenceWip}
                    isPersonInfoWip={isPersonInfoWip}
                    handleSnackOpen={handleSnackOpen}
                />
            </Col>

            <Container style={{ display: 'flex', flex: 2.5 }}>
                <Col>
                    {/* //$ Banner on Top For Special Messages */}
                    {bannerConditionalRender()}

                    <Row style={{ width: '100%', height: '75vh' }}>
                        {/* //& PROFILE VIEW */}
                        {show === 2 && (
                            <EditProfile
                                clinicianProfile={clinicianProfile}
                                basicValidation={basicValidation}
                                isPersonInfoWip={isPersonInfoWip}
                                setIsPersonInfoWip={setIsPersonInfoWip}
                                handleClear={handleClear}
                                setProfileDetails={setProfileDetails}
                            />
                        )}

                        {/* //& Licenses View */}
                        {show === 3 && (
                            <EditLicense
                                licenses={licenses}
                                editLicense={editLicense}
                                setEditLicense={setEditLicense}
                                isAddLicenceWip={isAddLicenceWip}
                                setIsAddLicenceWip={setIsAddLicenceWip}
                                isEditLicenceWip={isEditLicenceWip}
                                handleSnackOpen={handleSnackOpen}
                                setLoading={setLoading}
                                licenseEventListener={licenseEventListener}
                            />
                        )}

                        {/* //& CALENDLY VIEW */}
                        {show === 4 && <EditCalendar clinicianProfile={clinicianProfile} setCalendarLink={setCalendarLink} updateClinicianCalendar={updateClinicianCalendar} />}

                        {/* //& TEMPLATES VIEW */}
                        {show === 5 && <ClinicianTemplates  />}
                    </Row>
                </Col>

                {/* //& Confirmation Modal */}
                <ConfirmationModal profileModal={profileModal} handleClose={handleClose} updateClinicianProfile={updateClinicianProfile} />
            </Container>

            {/* //& Snackbar */}
            <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <SnackStyleAlert onClose={handleSnackClose} severity={snackSeverity}>
                    {snackText}
                </SnackStyleAlert>
            </Snackbar>
        </Row>
    )
}

const styles = {
    container: {
        background: 'rgba(255,255,255,0.5)',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100000,
    },
    drawerContainer: {
        display: 'flex',
        flex: 1,
        width: '100%',
        minWidth: '270px',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
    },
}

function SnackStyleAlert(props) {
    return <MuiAlert elevation={6} variant="filled" {...props} />
}

export default Profile;
